import type { JSONContent } from '@tiptap/vue-3';

import { safeJSONParse } from '~kui/utils/json';

const MAX_DEPTH = 100;

/**
 * Recursively sanitize TipTap JSON content to remove empty text nodes.
 * TipTap throws "Empty text nodes are not allowed" when text nodes have empty strings.
 */
const sanitizeTipTapContent = (content: JSONContent, depth = 0): JSONContent => {
  if (!content || depth > MAX_DEPTH) {
    return content;
  }

  // If this is a text node with empty text, return null to filter it out
  if (content.type === 'text' && (!content.text || content.text === '')) {
    return null as unknown as JSONContent;
  }

  // If this node has content array, recursively sanitize and filter out nulls
  if (content.content && Array.isArray(content.content)) {
    const sanitizedContent = content.content
      .map(child => sanitizeTipTapContent(child, depth + 1))
      .filter(child => child !== null);

    // If all content was removed, return node without content array
    // (empty paragraph is valid, paragraph with empty text array is not)
    if (sanitizedContent.length === 0) {
      const { content: _, ...rest } = content;
      return rest;
    }

    return {
      ...content,
      content: sanitizedContent,
    };
  }

  return content;
};

export const parseStringToTipTapJson = (str: string): JSONContent => {
  const json = safeJSONParse<JSONContent>(str);

  if (json) {
    // Sanitize parsed JSON to remove any empty text nodes
    return sanitizeTipTapContent(json);
  }

  // Empty or whitespace-only strings get an empty paragraph (no text node)
  if (!str || str.trim() === '') {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    };
  }

  // Non-empty plain text gets wrapped in a text node
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [{ type: 'text', text: str }],
      },
    ],
  };
};

/**
 * A composable to be used with KUICoreRichTextEditor
 *
 * @param dbData - string ref (or stringified version of the tiptap JSON content) to be kept in sync with updated tiptap editor json content
 * @returns model that can be used on the KUICoreRichTextEditor component
 */
export const useRichTextEditor = (dbData: Ref<string>) => {
  const rteModel = ref<JSONContent | null>(null); // use as the model on the KUICoreRichTextEditor component

  if (dbData) {
    // load initial string value into tiptap JSON
    rteModel.value = dbData ? parseStringToTipTapJson(dbData.value) : null;

    watch(
      () => rteModel.value,
      (val) => {
        if (val === null) {
          dbData.value = '';
          return;
        }

        if (typeof val === 'string') {
          dbData.value = val;
          return;
        }

        dbData.value = JSON.stringify(rteModel.value);
      },
      { deep: true },
    );
  }

  return {
    rteModel,
  };
}
